import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline, ThemeProvider,
    Stack, TextField, Typography, useMediaQuery,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Tooltip, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {Add, Delete, VpnKey, Warning, MenuBook, Public, Close} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import SideBar from "../Componant/sidebar/Sidebar";
import CodeBlock from "../Componant/CodeBlock";
import {listApiKeys, createApiKey, revokeApiKey} from "../Utils/apiKeysApi";
import {isValidUrl} from "../Utils/urlUtils";

function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('fr-FR');
}

function OriginsCell({origins}) {
    if (!origins || origins.length === 0) {
        return (
            <Tooltip title="Utilisable depuis n'importe quelle origine (usage serveur/CLI compris)">
                <Chip size="small" icon={<Public fontSize="small"/>} label="Toute origine" variant="outlined"/>
            </Tooltip>
        );
    }
    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {origins.map((origin) => (
                <Chip key={origin} size="small" label={origin} variant="outlined"/>
            ))}
        </Stack>
    );
}

function OriginsEditor({origins, onChange}) {
    const [draft, setDraft] = useState('');
    const [error, setError] = useState(null);

    const addOrigin = () => {
        const value = draft.trim();
        if (!value) return;
        if (!isValidUrl(value)) {
            setError('URL invalide (doit commencer par http:// ou https://)');
            return;
        }
        const origin = new URL(value).origin;
        if (!origins.includes(origin)) onChange([...origins, origin]);
        setDraft('');
        setError(null);
    };

    const removeOrigin = (origin) => onChange(origins.filter((o) => o !== origin));

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>Restreindre à des URLs (optionnel)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                Laissez vide pour autoriser toute origine (usage serveur/CLI). Sinon, seules les apps web servies
                depuis ces URLs pourront utiliser cette clé.
            </Typography>
            <Stack direction="row" spacing={1}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="https://monapp.fr"
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOrigin(); } }}
                    error={!!error}
                    helperText={error || ' '}
                />
                <Button variant="outlined" onClick={addOrigin} sx={{flexShrink: 0, height: 40}}>Ajouter</Button>
            </Stack>
            {origins.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{mt: 1}}>
                    {origins.map((origin) => (
                        <Chip key={origin} label={origin} size="small" onDelete={() => removeOrigin(origin)}/>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function ApiKeysPage() {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();
    const navigate = useNavigate();

    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);

    const [createOpen, setCreateOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyOrigins, setNewKeyOrigins] = useState([]);
    const [saving, setSaving] = useState(false);
    const [createdKey, setCreatedKey] = useState(null);

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    const refresh = () => {
        setLoading(true);
        listApiKeys()
            .then(setApiKeys)
            .catch((error) => console.error('Erreur lors du chargement des clés API:', error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
    }, []);

    const openCreateDialog = () => {
        setNewKeyName('');
        setNewKeyOrigins([]);
        setCreatedKey(null);
        setCreateOpen(true);
    };

    const closeCreateDialog = () => {
        setCreateOpen(false);
        if (createdKey) refresh();
    };

    const handleCreate = async () => {
        if (!newKeyName.trim()) return;
        setSaving(true);
        try {
            const apiKey = await createApiKey(newKeyName.trim(), newKeyOrigins);
            setCreatedKey(apiKey);
        } catch (error) {
            console.error('Erreur lors de la création de la clé API:', error);
            alert('Erreur lors de la création de la clé API');
        } finally {
            setSaving(false);
        }
    };

    const handleRevoke = async (apiKey) => {
        if (!window.confirm(`Révoquer la clé "${apiKey.name}" ? Toute intégration l'utilisant cessera de fonctionner.`)) return;
        try {
            await revokeApiKey(apiKey.id);
            refresh();
        } catch (error) {
            console.error('Erreur lors de la révocation:', error);
            alert('Erreur lors de la révocation de la clé API');
        }
    };

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{padding: {xs: 2, sm: 4}, margin: '0 auto', width: '100%'}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="space-between" alignItems={{xs: 'stretch', sm: 'center'}} mb={3}>
                            <Typography variant="h4">Clés API</Typography>
                            <Stack direction="row" spacing={1.5}>
                                <Button variant="outlined" color="primary" startIcon={<MenuBook/>} onClick={() => navigate('/api-docs')}>
                                    Documentation
                                </Button>
                                <Button variant="contained" color="primary" startIcon={<Add/>} onClick={openCreateDialog}>
                                    Créer une clé
                                </Button>
                            </Stack>
                        </Stack>

                        <Box sx={{px: {xs: 0, sm: 2}}}>
                            <Typography color="text.secondary" sx={{mb: 3}}>
                                Créez une clé par application (site, script, intégration) et restreignez-la aux
                                URLs qui doivent pouvoir l'utiliser.
                            </Typography>

                            {loading ? (
                                <Typography>Chargement...</Typography>
                            ) : apiKeys.length === 0 ? (
                                <Typography color="text.secondary">
                                    Aucune clé API pour le moment. Créez-en une pour utiliser l'API à distance.
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Nom</TableCell>
                                                <TableCell>Clé</TableCell>
                                                <TableCell>Origines autorisées</TableCell>
                                                <TableCell>Créée le</TableCell>
                                                <TableCell>Dernière utilisation</TableCell>
                                                <TableCell>Statut</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {apiKeys.map((apiKey) => (
                                                <TableRow key={apiKey.id} hover>
                                                    <TableCell sx={{wordBreak: 'break-word'}}>{apiKey.name}</TableCell>
                                                    <TableCell sx={{fontFamily: 'monospace'}}>{apiKey.keyPrefix}••••••••</TableCell>
                                                    <TableCell sx={{minWidth: 200}}><OriginsCell origins={apiKey.allowedOrigins}/></TableCell>
                                                    <TableCell>{formatDate(apiKey.createdAt)}</TableCell>
                                                    <TableCell>{formatDate(apiKey.lastUsedAt)}</TableCell>
                                                    <TableCell>
                                                        {apiKey.revokedAt ? (
                                                            <Chip size="small" label="Révoquée" color="default" variant="outlined"/>
                                                        ) : (
                                                            <Chip size="small" label="Active" color="success" variant="outlined"/>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {!apiKey.revokedAt && (
                                                            <Tooltip title="Révoquer">
                                                                <IconButton onClick={() => handleRevoke(apiKey)}>
                                                                    <Delete fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <VpnKey/>
                                <span>Nouvelle clé API</span>
                            </Stack>
                            <IconButton size="small" onClick={closeCreateDialog}><Close fontSize="small"/></IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        {!createdKey ? (
                            <Stack spacing={3} sx={{mt: 1}}>
                                <TextField
                                    autoFocus
                                    label="Nom de la clé"
                                    placeholder="Ex. Site vitrine, Script de production..."
                                    fullWidth
                                    value={newKeyName}
                                    onChange={(e) => setNewKeyName(e.target.value)}
                                />
                                <OriginsEditor origins={newKeyOrigins} onChange={setNewKeyOrigins}/>
                            </Stack>
                        ) : (
                            <Stack spacing={2} sx={{mt: 1}}>
                                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{
                                    bgcolor: 'warning.main', color: 'warning.contrastText',
                                    p: 1.5, borderRadius: 1, opacity: 0.9,
                                }}>
                                    <Warning fontSize="small"/>
                                    <Typography variant="body2">
                                        Copiez cette clé maintenant : elle ne sera plus jamais affichée.
                                    </Typography>
                                </Stack>
                                <CodeBlock copyValue={createdKey.key}>{createdKey.key}</CodeBlock>
                                <OriginsCell origins={createdKey.allowedOrigins}/>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {!createdKey ? (
                            <>
                                <Button onClick={closeCreateDialog}>Annuler</Button>
                                <Button variant="contained" color="primary" onClick={handleCreate} disabled={saving || !newKeyName.trim()}>
                                    {saving ? 'Création...' : 'Créer'}
                                </Button>
                            </>
                        ) : (
                            <Button variant="contained" color="primary" onClick={closeCreateDialog}>
                                J'ai copié ma clé, fermer
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </div>
    );
}

export default ApiKeysPage;
